import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    PredefinedCommentFormContext,
    PredefinedCommentFormMode,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/contexts/predefined-comment-form.context';
import { usePredefinedCommentFetcher } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-fetcher.hook';
import { settingsModulePaths } from 'core/routes/routes';
import { showErrorNotification } from 'core/utils/notification-service';
import { useTranslation } from 'core/i18n';

interface PredefinedCommentFormProviderProps {
    children: React.ReactNode;
}

const getModeFromId = (id: string | undefined): PredefinedCommentFormMode => {
    if (!id) {
        return 'add';
    }
    return id === 'new' ? 'add' : 'edit';
};

export const PredefinedCommentFormProvider: React.FC<PredefinedCommentFormProviderProps> = ({ children }) => {
    const { predefinedCommentId } = useParams<{ predefinedCommentId: string }>();
    const { predefinedComment, isFetching, isError } = usePredefinedCommentFetcher(predefinedCommentId);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const mode = getModeFromId(predefinedCommentId);
    const isFormLoading = mode === 'edit' && isFetching;

    // On a failed edit-mode load, notify and return to the list (mirrors the legacy catch).
    useEffect(() => {
        if (mode === 'edit' && isError) {
            showErrorNotification(t('settings.devices.predefined-comments.form.notification.fetch.error'));
            navigate(settingsModulePaths.predefinedComments);
        }
    }, [mode, isError, t, navigate]);

    const value = useMemo(
        () => ({
            mode,
            predefinedComment: mode === 'add' ? undefined : predefinedComment,
            isFormLoading,
            predefinedCommentId,
        }),
        [mode, predefinedComment, isFormLoading, predefinedCommentId]
    );

    return <PredefinedCommentFormContext.Provider value={value}>{children}</PredefinedCommentFormContext.Provider>;
};
